/**
 * Retry Utility with Exponential Backoff
 *
 * Automatically retry failed API calls with increasing delays
 */

import { logger } from '../services/logger';
import { API_CONFIG } from '../constants';

export interface RetryOptions {
  maxAttempts?: number; // Default: 3
  baseDelay?: number; // Default: 1000ms (1 second)
  maxDelay?: number; // Default: 10000ms (10 seconds)
  onRetry?: (attempt: number, error: any) => void;
}

/**
 * Sleep utility
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Calculate exponential backoff delay
 */
function calculateDelay(attempt: number, baseDelay: number, maxDelay: number): number {
  // Exponential: 1s, 2s, 4s, 8s, etc.
  const delay = baseDelay * Math.pow(2, attempt);
  return Math.min(delay, maxDelay);
}

/**
 * Retry a function with exponential backoff
 *
 * @example
 * const data = await retryWithBackoff(
 *   async () => await fetch('https://api.example.com/data'),
 *   { maxAttempts: 3, baseDelay: 1000 }
 * );
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxAttempts = API_CONFIG.RETRY_ATTEMPTS,
    baseDelay = API_CONFIG.RETRY_BASE_DELAY,
    maxDelay = API_CONFIG.RETRY_MAX_DELAY,
    onRetry
  } = options;

  let lastError: any;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      // Try to execute the function
      const result = await fn();

      // Success - log if it was a retry
      if (attempt > 0) {
        logger.info('Retry', `Success after ${attempt + 1} attempts`);
      }

      return result;
    } catch (error) {
      lastError = error;

      // If this was the last attempt, throw the error
      if (attempt === maxAttempts - 1) {
        logger.error('Retry', `Failed after ${maxAttempts} attempts`, error);
        throw error;
      }

      // Calculate delay for next retry
      const delay = calculateDelay(attempt, baseDelay, maxDelay);

      // Log the retry
      logger.warn('Retry', `Attempt ${attempt + 1} failed, retrying in ${delay}ms`, { error: String(error) });

      // Call onRetry callback if provided
      if (onRetry) {
        onRetry(attempt + 1, error);
      }

      // Wait before retrying
      await sleep(delay);
    }
  }

  // Should never reach here, but TypeScript needs this
  throw lastError;
}

/**
 * Retry fetch requests with exponential backoff
 */
export async function fetchWithRetry(
  url: string,
  options?: RequestInit,
  retryOptions?: RetryOptions
): Promise<Response> {
  return retryWithBackoff(
    async () => {
      const response = await fetch(url, options);

      // Consider 5xx errors as retryable
      if (response.status >= 500 && response.status < 600) {
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }

      return response;
    },
    retryOptions
  );
}

/**
 * Check if an error is network-related (and should be retried)
 */
export function isNetworkError(error: any): boolean {
  const networkErrors = [
    'Failed to fetch',
    'NetworkError',
    'ECONNREFUSED',
    'ETIMEDOUT',
    'ENOTFOUND',
    'Service Unavailable',
    'Gateway Timeout'
  ];

  const errorMessage = String(error?.message || error);
  return networkErrors.some(msg => errorMessage.includes(msg));
}
